import { ItemType } from "@core/FileStructue/Item/ItemType";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import BugsnagTrigger from "@ext/bugsnag/components/BugsnagTrigger";
import { NotificationSettingsButton } from "@ext/enterprise/components/NotificationSettingsButton";
import { useIsEnterprise } from "@ext/enterprise/utils/useIsEnterprise";
import { useIsAnyGes } from "@ext/enterpriseCommon/hooks/useIsAnyGes";
import { ArticleRevisionTrigger } from "@ext/git/actions/Revisions/components/ArticleRevisionTrigger";
import { DeleteItemTrigger } from "@ext/item/actions/DeleteArticleTrigger";
import { SearchInScopeTrigger } from "@ext/item/actions/SearchInScopeTrigger";
import t from "@ext/localization/locale/translate";
import {
	isReadOnlyGroup,
	isUnnestableArticle,
	useNavigationTreeStore,
} from "@ext/navigation/catalog/SidebarNavigation/store/navigationTreeStore";
import { DropdownMenuLabel, DropdownMenuSeparator } from "@ui-kit/Dropdown";
import React, { type Dispatch, type SetStateAction } from "react";
import type { Environment } from "../../../app/resolveModule/env";
import ExportToDocxOrPdf from "../../components/Actions/ExportToDocxOrPdf";
import ShowInExplorer from "../../components/Actions/ShowInExplorer";
import { usePlatform } from "../../ui-logic/hooks/usePlatform";
import ArticleMoveAction from "../article/actions/move/ArticleMoveAction";
import ShareAction from "../catalog/actions/share/components/ShareAction";
import { ArticleFavoriteSettingsButton } from "../enterprise/components/ArticleFavoriteSettingsButton";
import EnterpriseCheckStyleGuide from "../enterprise/components/EnterpriseCheckStyleGuide";
import type { CategoryLink, ItemLink } from "../navigation/NavigationLinks";
import ArticleLinks from "../properties/components/Helpers/ArticleLinks";
import TemplateItemList from "../templates/components/TemplateItemList";
import PropsEditorTrigger from "./actions/propsEditor/components/PropsEditorTrigger";

interface EditMenuProps {
	itemLink: ItemLink;
	setItemLink: Dispatch<SetStateAction<ItemLink>>;
	onExpandedChange?: (itemPath: string, expanded: boolean) => void;
	onOpen?: () => void;
	onClose?: () => void;
}

const HeaderLabel: React.FC = () => (
	<DropdownMenuLabel className="text-primary-fg">{t("article.actions.title")}</DropdownMenuLabel>
);

const components: Record<Environment, (props: EditMenuProps) => React.ReactNode> = {
	web: (props) => <AnyEditorEditMenu {...props} />,
	tauri: (props) => <AnyEditorEditMenu {...props} />,
	next: (props) => <ReadonlyEditMenu {...props} />,
	static: (props) => <ReadonlyEditMenu {...props} />,
	cli: (props) => <ReadonlyEditMenu {...props} />,
	docportal: (props) => <ReadonlyEditMenu {...props} />,
	test: () => null,
};

const EditMenu = React.memo((props: EditMenuProps) => {
	const { environment } = usePlatform();
	return components[environment](props);
});

export default EditMenu;

const isCategoryLink = (itemLink: ItemLink) =>
	itemLink.type === ItemType.category && (itemLink as CategoryLink).items.length > 0;

const ReadonlyEditMenu = ({ itemLink }: EditMenuProps) => {
	const isCategory = isCategoryLink(itemLink);

	if (itemLink.options?.isHasErrorCode) return null;

	return (
		<>
			<HeaderLabel />
			<DropdownMenuSeparator />
			<SearchInScopeTrigger isCategory={isCategory} itemLink={itemLink} />
			<ArticleFavoriteSettingsButton itemLinkPath={itemLink.ref.path} />
			<DropdownMenuSeparator />
			<ExportToDocxOrPdf
				fileName={itemLink.fileName}
				isCategory={isCategory}
				itemRefPath={itemLink.ref.path}
				title={itemLink.title}
			/>
		</>
	);
};

const AnyEditorEditMenu = (props: EditMenuProps) => {
	const isReadOnly = PageDataContextService.value.conf.isReadOnly;
	const limitedActions = useCatalogPropsStore((state) => !!state.data?.behavior.limitedActions);
	if (isReadOnly) return <ReadonlyEditMenu {...props} />;
	// Its items are only deleted, and their trigger deletes them itself: see `useIsDeleteOnlyItem`.
	if (limitedActions) return null;
	return <EditorEditMenu {...props} />;
};

/** A delete button in place of a menu of one item; `ReadonlyEditMenu` (read-only catalog, DocPortal) keeps its menu. */
export const useIsDeleteOnlyItem = (): boolean => {
	const { environment } = usePlatform();
	const isReadOnly = PageDataContextService.value.conf.isReadOnly;
	const limitedActions = useCatalogPropsStore((state) => !!state.data?.behavior.limitedActions);
	const isEditor = environment === "web" || environment === "tauri";
	return isEditor && !isReadOnly && limitedActions;
};

export const useHasItemActions = (itemLink: ItemLink | null, topLevelLinks: ItemLink[]): boolean => {
	const behavior = useCatalogPropsStore((state) => state.data?.behavior);
	const isTopLevelGroup =
		itemLink?.type === ItemType.category && topLevelLinks.some((link) => link.ref.path === itemLink.ref.path);
	return !(behavior?.limitedActions && behavior.readOnlyTopCategories && isTopLevelGroup);
};

const EditorEditMenu = ({ itemLink, setItemLink, onExpandedChange }: EditMenuProps) => {
	const isCategory = isCategoryLink(itemLink);
	const isLinkToValidArticle = !itemLink.options?.isHasErrorCode;
	const isCurrentItem = useArticlePropsStore((state) => state?.data.ref?.path === itemLink.ref.path);

	const isEnterprise = useIsEnterprise();
	const isAnyGes = useIsAnyGes();
	// Same rule as the catalog's share action: an unpublished catalog has nothing to link to.
	const hasSource = useCatalogPropsStore((state) => !!state.data?.sourceName);
	const isReadOnlyItem = useNavigationTreeStore((s) => isReadOnlyGroup(s, itemLink.ref.path));
	const isUnnestable = useNavigationTreeStore((s) => isUnnestableArticle(s, itemLink.ref.path));
	const canChange = isLinkToValidArticle && !isReadOnlyItem;

	return (
		<>
			<HeaderLabel />
			{canChange && (
				<>
					<PropsEditorTrigger
						isCategory={isCategory}
						itemLink={itemLink}
						onExpandedChange={onExpandedChange}
						setItemLink={setItemLink}
					/>
					<TemplateItemList itemRefPath={itemLink.ref.path} />
				</>
			)}
			{!isReadOnlyItem && !isUnnestable && (
				<ArticleMoveAction articlePath={itemLink.ref.path} itemLink={itemLink} />
			)}
			{isLinkToValidArticle && <ArticleFavoriteSettingsButton itemLinkPath={itemLink.ref.path} />}
			{isLinkToValidArticle && <SearchInScopeTrigger isCategory={isCategory} itemLink={itemLink} />}
			<DropdownMenuSeparator />
			{isLinkToValidArticle && (
				<>
					{hasSource && <ShareAction copyOnly={isAnyGes} isArticle path={itemLink.pathname} />}
					<ArticleLinks itemRefPath={itemLink.ref.path} />
					<DropdownMenuSeparator />
				</>
			)}
			{isLinkToValidArticle && isCurrentItem && <EnterpriseCheckStyleGuide />}
			{isLinkToValidArticle && <ArticleRevisionTrigger itemLink={itemLink} />}
			<DropdownMenuSeparator />
			{isLinkToValidArticle && (
				<ExportToDocxOrPdf
					fileName={itemLink.fileName}
					isCategory={isCategory}
					itemRefPath={itemLink.ref.path}
					title={itemLink.title}
				/>
			)}
			{isEnterprise && (
				<>
					<DropdownMenuSeparator />
					<NotificationSettingsButton itemRefPath={itemLink.ref.path} />
				</>
			)}
			<ShowInExplorer item={itemLink} />
			<DropdownMenuSeparator />
			<BugsnagTrigger itemLogicPath={itemLink.ref.path} />
			{!isReadOnlyItem && (
				<>
					<DropdownMenuSeparator />
					<DeleteItemTrigger itemLink={itemLink} />
				</>
			)}
		</>
	);
};
